import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money, fmtDate, onlyDigits } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Lock, Unlock, CheckCircle2, AlertTriangle, MessageCircle,
  Pencil, Loader2, Calendar, MapPinned, UserCog, Wallet, Users, CalendarClock,
} from "lucide-react";

const DIAS_FULL = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const METODO_LABEL = { dinheiro: "💵 Dinheiro", pix: "⚡ Pix", credito: "💳 Crédito", debito: "💳 Débito" };

export default function ConferenciaRota() {
  const { rid } = useParams();
  const nav = useNavigate();
  const todayIso = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState(todayIso);
  const [confData, setConfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // modal: {mode: "baixa"|"corrigir", venda_id, parcela: {...}}
  const [modal, setModal] = useState(null);
  const [modalValor, setModalValor] = useState("");
  const [modalMetodo, setModalMetodo] = useState("dinheiro");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/rotas/${rid}/conferencia`, { params: { data } });
      setConfData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar conferência");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid, data]);

  const finalizada = !!confData?.finalizada;

  const openBaixa = (p) => {
    setModal({ mode: "baixa", venda_id: p.venda_id, parcela: p });
    setModalValor(String((p.valor - p.valor_pago).toFixed(2)));
    setModalMetodo(p.metodo_pagamento || "dinheiro");
  };
  const openCorrigir = (p) => {
    setModal({ mode: "corrigir", venda_id: p.venda_id, parcela: p });
    setModalValor(String(p.valor_pago.toFixed(2)));
    setModalMetodo(p.metodo_pagamento || "dinheiro");
  };

  const confirmModal = async () => {
    if (finalizada) return toast.error("Rota já finalizada. Reabra para editar.");
    const v = parseFloat(modalValor);
    if (isNaN(v) || v < 0) return toast.error("Valor inválido");
    setBusy(true);
    try {
      if (modal.mode === "baixa") {
        if (!(v > 0)) throw new Error("Informe um valor");
        await api.post(`/vendas/${modal.venda_id}/parcelas/${modal.parcela.parcela_id}/pagar`, {
          valor_pago: v, metodo_pagamento: modalMetodo,
        });
        toast.success("Baixa registrada");
      } else {
        await api.post(`/vendas/${modal.venda_id}/parcelas/${modal.parcela.parcela_id}/corrigir`, {
          valor_pago: v, metodo_pagamento: v > 0 ? modalMetodo : null,
        });
        toast.success("Pagamento corrigido");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Erro");
    } finally {
      setBusy(false);
    }
  };

  const finalizarRota = async () => {
    if (!window.confirm("Finalizar a conferência dessa rota nesse dia? Enquanto estiver finalizada, nenhum pagamento pode ser alterado.")) return;
    setBusy(true);
    try {
      await api.post(`/rotas/${rid}/conferencia/finalizar`, { data });
      toast.success("Rota finalizada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  const reabrirRota = async () => {
    if (!window.confirm("Reabrir essa conferência? Pagamentos voltam a ficar editáveis.")) return;
    setBusy(true);
    try {
      await api.post(`/rotas/${rid}/conferencia/reabrir`, { data });
      toast.success("Rota reaberta");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  // Agrupa parcelas por cliente para melhor UX
  const grupos = useMemo(() => {
    if (!confData?.parcelas) return [];
    const map = new Map();
    for (const p of confData.parcelas) {
      if (!map.has(p.cliente_id)) {
        map.set(p.cliente_id, {
          cliente_id: p.cliente_id,
          cliente_nome: p.cliente_nome,
          cliente_telefone: p.cliente_telefone,
          cobrador_name: p.cobrador_name,
          parcelas: [],
        });
      }
      map.get(p.cliente_id).parcelas.push(p);
    }
    return Array.from(map.values());
  }, [confData]);

  return (
    <div>
      <button onClick={() => nav("/rotas")} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-3" data-testid="btn-back-rotas">
        <ArrowLeft className="w-4 h-4" /> Voltar para rotas
      </button>

      <header className="mb-4">
        <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--terracotta)] inline-flex items-center gap-1">
          <MapPinned className="w-3.5 h-3.5" /> Conferência de Rota
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">
          {confData?.rota?.nome || "..."}
        </h1>
        {confData?.rota && (
          <p className="text-sm text-stone-500 mt-1">
            Dia da rota: <b>{DIAS_FULL[confData.rota.dia_semana]}</b>
          </p>
        )}
      </header>

      <div className="carne-card p-4 mb-4" data-testid="conf-filters">
        <label className="text-xs font-bold uppercase tracking-wider text-stone-500 inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Dia da conferência
        </label>
        <input
          data-testid="input-conf-data"
          type="date"
          value={data}
          onChange={(e) => setData(e.target.value)}
          className="mt-1 w-full h-11 px-3 rounded-lg border border-stone-200 bg-white"
        />
      </div>

      {loading && <div className="text-stone-500 text-sm">Carregando…</div>}

      {!loading && confData && (
        <>
          {finalizada && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-3 mb-4 flex items-start gap-2" data-testid="conf-finalizada-badge">
              <Lock className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-emerald-900 text-sm">Rota finalizada</div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Finalizada {confData.finalizada.at ? fmtDate(confData.finalizada.at.slice(0, 10)) : "—"}
                  {confData.finalizada.por_name ? ` por ${confData.finalizada.por_name}` : ""}.
                  Reabra para editar algum pagamento.
                </div>
              </div>
              <button
                data-testid="btn-reabrir-rota"
                onClick={reabrirRota}
                disabled={busy}
                className="h-9 px-3 rounded-full bg-white text-emerald-800 border border-emerald-300 text-xs font-bold inline-flex items-center gap-1 hover:bg-emerald-100"
              >
                <Unlock className="w-3.5 h-3.5" /> Reabrir
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="conf-resumo">
            <div className="rounded-xl bg-white border border-stone-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Previsto
              </div>
              <div className="font-mono-num text-lg font-bold text-stone-900">{money(confData.resumo.total_previsto)}</div>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Recebido
              </div>
              <div className="font-mono-num text-lg font-bold text-emerald-900">{money(confData.resumo.total_recebido)}</div>
            </div>
            <div className={`rounded-xl p-3 border ${confData.resumo.total_pendente > 0 ? "bg-rose-50 border-rose-200" : "bg-stone-50 border-stone-200"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${confData.resumo.total_pendente > 0 ? "text-rose-700" : "text-stone-500"}`}>
                <AlertTriangle className="w-3 h-3" /> Pendente
              </div>
              <div className={`font-mono-num text-lg font-bold ${confData.resumo.total_pendente > 0 ? "text-rose-900" : "text-stone-900"}`}>{money(confData.resumo.total_pendente)}</div>
            </div>
            <div className={`rounded-xl p-3 border ${(confData.resumo.total_remarcado || 0) > 0 ? "bg-amber-50 border-amber-200" : "bg-stone-50 border-stone-200"}`} data-testid="conf-total-remarcado">
              <div className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${(confData.resumo.total_remarcado || 0) > 0 ? "text-amber-800" : "text-stone-500"}`}>
                <CalendarClock className="w-3 h-3" /> Remarcado
              </div>
              <div className={`font-mono-num text-lg font-bold ${(confData.resumo.total_remarcado || 0) > 0 ? "text-amber-900" : "text-stone-900"}`}>{money(confData.resumo.total_remarcado || 0)}</div>
            </div>
          </div>

          <div className="text-xs text-stone-500 mb-3 inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> {grupos.length} clientes • {confData.resumo.parcelas_pagas}/{confData.resumo.parcelas_total} parcelas pagas
          </div>

          {grupos.length === 0 && (
            <div className="carne-card p-8 text-center text-stone-500 text-sm" data-testid="conf-empty">
              Nenhuma parcela dessa rota cai em <b>{fmtDate(data)}</b>.
            </div>
          )}

          <div className="space-y-3" data-testid="conf-grupos">
            {grupos.map((g) => (
              <div key={g.cliente_id} className="carne-card p-4" data-testid={`conf-cliente-${g.cliente_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <Link to={`/clientes/${g.cliente_id}`} className="font-display text-base font-bold text-stone-900 hover:underline block truncate">
                      {g.cliente_nome}
                    </Link>
                    <div className="text-[11px] text-stone-500 inline-flex items-center gap-1">
                      {g.cobrador_name ? (<><UserCog className="w-3 h-3" /> {g.cobrador_name}</>) : "Sem cobrador"}
                    </div>
                  </div>
                  {g.cliente_telefone && (
                    <a
                      href={`https://wa.me/${onlyDigits(g.cliente_telefone)}`}
                      target="_blank" rel="noreferrer"
                      className="h-9 w-9 rounded-full bg-[color:var(--whatsapp-light)] text-[color:var(--whatsapp-dark)] inline-flex items-center justify-center shrink-0"
                      data-testid={`btn-whats-${g.cliente_id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="divide-y divide-stone-100">
                  {g.parcelas.map((p) => (
                    <div key={p.parcela_id} className={`py-2 flex items-center justify-between gap-2 ${p.remarcada_na_data ? "bg-amber-50/60 -mx-4 px-4 border-l-4 border-amber-400" : ""}`} data-testid={`conf-parcela-${p.parcela_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-stone-900">Parc. {p.parcela_numero}/{p.total_parcelas}</span>
                          {p.remarcada_na_data ? (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900 bg-amber-200 px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`conf-badge-remarcado-${p.parcela_id}`}>
                              <CalendarClock className="w-3 h-3" /> Remarcado
                            </span>
                          ) : (
                            <>
                              {p.status === "paga" && <span className="stamp-paid">Paga</span>}
                              {p.status === "parcial" && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Parcial</span>}
                              {p.status === "atrasada" && <span className="stamp-overdue">Atrasada</span>}
                            </>
                          )}
                          {p.metodo_pagamento && !p.remarcada_na_data && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded">
                              {METODO_LABEL[p.metodo_pagamento]}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-stone-500 truncate">
                          {p.descricao} • Vence {fmtDate(p.vencimento)}
                        </div>
                        {p.remarcada_na_data && p.remarcacao_atual ? (
                          <div className="mt-1 text-[11px] bg-white border border-amber-200 rounded-lg px-2 py-1.5 space-y-0.5" data-testid={`conf-rem-info-${p.parcela_id}`}>
                            <div><b className="text-amber-900">Data original:</b> {fmtDate(p.remarcacao_atual.data_original)}</div>
                            <div><b className="text-amber-900">Nova data:</b> {fmtDate(p.remarcacao_atual.nova_data)}</div>
                            <div><b className="text-amber-900">Motivo:</b> {p.remarcacao_atual.motivo}</div>
                            <div className="text-stone-500">
                              Por <b>{p.remarcacao_atual.por_name || "—"}</b> em {p.remarcacao_atual.at ? new Date(p.remarcacao_atual.at).toLocaleString("pt-BR") : "—"}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-stone-700 mt-0.5">
                            <span className="font-mono-num">{money(p.valor_pago)}</span>
                            <span className="text-stone-400"> / </span>
                            <span className="font-mono-num">{money(p.valor)}</span>
                          </div>
                        )}
                      </div>
                      {!p.remarcada_na_data && (
                        <div className="flex flex-col gap-1 shrink-0">
                          {p.status !== "paga" && !finalizada && (
                            <button
                              data-testid={`btn-conf-baixa-${p.parcela_id}`}
                              onClick={() => openBaixa(p)}
                              disabled={busy}
                              className="h-8 px-3 rounded-full bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1 hover:bg-emerald-700"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Baixar
                            </button>
                          )}
                          {!finalizada && (
                            <button
                              data-testid={`btn-conf-corrigir-${p.parcela_id}`}
                              onClick={() => openCorrigir(p)}
                              disabled={busy}
                              className="h-8 px-3 rounded-full bg-white border border-stone-300 text-stone-700 text-xs font-bold inline-flex items-center gap-1 hover:bg-stone-50"
                            >
                              <Pencil className="w-3 h-3" /> Corrigir
                            </button>
                          )}
                        </div>
                      )}
                      {p.remarcada_na_data && (
                        <div className="text-right shrink-0">
                          <div className="font-mono-num text-sm font-bold text-amber-900">{money(p.valor)}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {!finalizada && grupos.length > 0 && (
            <button
              data-testid="btn-finalizar-rota"
              onClick={finalizarRota}
              disabled={busy}
              className="btn-primary w-full mt-6"
              style={{ background: "var(--coal)" }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Finalizar conferência
            </button>
          )}
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="conf-modal">
            <h3 className="font-display text-lg font-bold text-stone-900 mb-1">
              {modal.mode === "baixa" ? "Dar baixa" : "Corrigir pagamento"}
            </h3>
            <p className="text-sm text-stone-500 mb-3">
              {modal.parcela.cliente_nome} — Parc. {modal.parcela.parcela_numero}/{modal.parcela.total_parcelas}
            </p>
            {modal.mode === "corrigir" && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                Correção substitui o valor pago (ex: baixa indevida vira 0).
              </div>
            )}
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">
              {modal.mode === "baixa" ? "Valor recebido (R$)" : "Novo valor total pago (R$)"}
            </label>
            <input
              data-testid="conf-modal-valor"
              type="number"
              step="0.01"
              min="0"
              value={modalValor}
              onChange={(e) => setModalValor(e.target.value)}
              autoFocus
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
            />
            {parseFloat(modalValor) > 0 && (
              <div className="mt-3">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Forma de pagamento</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  {[
                    { k: "dinheiro", label: "Dinheiro", emoji: "💵" },
                    { k: "pix", label: "Pix", emoji: "⚡" },
                    { k: "credito", label: "Crédito", emoji: "💳" },
                    { k: "debito", label: "Débito", emoji: "💳" },
                  ].map(({ k, label, emoji }) => (
                    <button
                      key={k}
                      type="button"
                      data-testid={`conf-modal-method-${k}`}
                      onClick={() => setModalMetodo(k)}
                      className={`h-11 rounded-xl border-2 font-bold text-sm inline-flex items-center justify-center gap-1 transition-colors ${
                        modalMetodo === k
                          ? "border-[color:var(--terracotta)] bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]"
                          : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                      }`}
                    >
                      <span>{emoji}</span> {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal(null)} className="flex-1 h-12 rounded-xl border border-stone-200 font-bold text-stone-700">Cancelar</button>
              <button data-testid="conf-modal-confirm" onClick={confirmModal} disabled={busy} className="btn-primary flex-1">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
